<template>
    <div>
        <div class="mb-6 flex justify-between items-center">
            <label class="text-sm font-bold font-gotham">
                {{ $t('PERSONALIZE_CHOOSE_OPTION') }}
            </label>

            <base-checkbox
                :checkbox-id="itemData.name"
                :model-value="mirrorPersonalization"
                @checkbox-checked="updateMirror"
            >
                {{ itemData.code.includes('Left') ? $t('PERSONALIZE_SAME_RIGHT') : $t('PERSONALIZE_SAME_LEFT') }}
            </base-checkbox>
        </div>

        <div class="flex flex-wrap gap-2.5">
            <div
                v-for="option in personalizationTypes"
                :key="option.id"
            >
                <swatch-button
                    :input-name="option.name"
                    :input-value="option.value"
                    :input-image="option.image"
                    :selected-value="selectedPersonalizationType"
                    variant="medium"
                    :label="option.label"
                    @valueChange="selectPersonalizationType(option, $event)"
                ></swatch-button>

                <div
                    v-if="option.label"
                    class="mt-4 h-5 flex items-start gap-3 w-1 pr-1 break-keep"
                >
                    <p class="text-sm font-bold font-gotham">
                        {{ option.label }}
                    </p>

                    <tooltip-button
                        v-if="option.modalContent"
                        :modal-heading="option.modalContent.title"
                        :modal-copy="option.modalContent.copy"
                        :modal-image="option.image"
                    />
                </div>
            </div>
        </div>

        <div class="mt-5 flex justify-end">
            <personalization-policy-button />
        </div>

        <div
            v-if="selectedPersonalizationType === 'personalization-logo'"
        >
            <div class="mt-7 pt-7 border-t border-gray-light">
            </div>

            <personalization-logo
                :placement="logoPlacementWithSelected"
                :mirror="mirrorPersonalization"
            ></personalization-logo>
        </div>

        <div
            v-if="selectedPersonalizationType === 'personalization-monogram'"
        >
            <div class="mt-7 pt-7 border-t border-gray-light">
            </div>

            <personalization-text
                :placement="textPlacement"
                :mirror="mirrorPersonalization"
            ></personalization-text>
        </div>
    </div>
</template>

<script>
    import BaseCheckbox from './BaseCheckbox.vue';
    import SwatchButton from './SwatchButton.vue';
    import TooltipButton from './TooltipButton.vue';
    import PersonalizationLogo from './PersonalizationLogo.vue';
    import PersonalizationText from './PersonalizationText.vue';
    import PersonalizationPolicyButton from './PersonalizationPolicyButton.vue';

    import IconNone from '../assets/images/personalization-none.png';
    import IconLogo from '../assets/images/personalization-logo.png';
    import IconMonogram from '../assets/images/personalization-monogram.png';

    import allStores from '../stores/all';

    export default {
        mixins: [allStores],

        components: {
            BaseCheckbox,
            SwatchButton,
            TooltipButton,
            PersonalizationLogo,
            PersonalizationText,
            PersonalizationPolicyButton,
        },

        props: {
            itemData: {
                type: Object,
                required: true,
            },
        },

        data() {
            return {
                mirrorPersonalization: false,
            };
        },

        computed: {
            viewController() {
                return this.customizerStore.viewController;
            },

            selectedPersonalizationType() {
                return this.customizerStore.selectedPersonalizationType(this.itemData);
            },

            personalizationTypes() {
                const types = [];

                let iconNone = null;
                let iconLogo = null;
                let iconText = null;

                let side = 'right';
                if (this.itemData.code.includes('Left')) {
                    side = 'left';
                }

                if (this.viewController.blueprint.custom[`fj-personalization-icon-logo-${side}`]) {
                    iconLogo = this.viewController.blueprint.custom[`fj-personalization-icon-logo-${side}`];
                }

                if (this.viewController.blueprint.custom[`fj-personalization-icon-none-${side}`]) {
                    iconNone = this.viewController.blueprint.custom[`fj-personalization-icon-none-${side}`];
                }

                if (this.viewController.blueprint.custom[`fj-personalization-icon-text-${side}`]) {
                    iconText = this.viewController.blueprint.custom[`fj-personalization-icon-text-${side}`];
                }

                types.push({
                    id: 'personalization-none',

                    name: this.itemData.code,
                    value: 'personalization-none',
                    label: this.$t('PERSONALIZE_NONE'),
                    image: iconNone ?? IconNone,

                });

                if (this.customizerStore.allowedLogos.size > 0) {
                    types.push({
                        id: 'personalization-logo',

                        name: this.itemData.code,
                        value: 'personalization-logo',
                        label: this.$t('PERSONALIZE_LOGO'),
                        image: iconLogo ?? IconLogo,

                    });
                }

                types.push({
                    id: 'personalization-monogram',

                    name: this.itemData.code,
                    value: 'personalization-monogram',
                    label: this.$t('PERSONALIZE_MONOGRAM'),
                    image: iconText ?? IconMonogram,

                });

                return types;
            },

            logoPlacement() {
                return this.itemData.placements.find((x) => x.code.startsWith('MONOGRAMMINGLOGO'));
            },

            logoPlacementWithSelected() {
                const placement = this.logoPlacement;

                const selected = this.viewController.selectedAtPlacement(placement.code);

                // eslint-disable-next-line no-unused-vars
                const actualSelected = this.customizerStore.selectedPersonalization(this.itemData);

                let code = selected?.code;
                let name = selected ? this.viewController.componentNames(selected).join(' ') : null;

                if (actualSelected?.code?.includes('MONOGRAMMINGIMAGE')) {
                    const ecomValue = actualSelected.custom['fj-parameter-value'];
                    if (ecomValue) {
                        code = `VENDORLOGO-${ecomValue}`;
                        name = actualSelected.custom['fj-parameter-value-name'];
                    }
                }

                return {
                    code: placement.code,
                    components: placement.components,
                    selectedValue: code,
                    selectedName: name,
                };
            },

            textPlacement() {
                return this.itemData.placements.find((x) => x.code.startsWith('MONOGRAMMINGTEXT'));
            },

            storeMirrorPersonalization() {
                return this.customizerStore.mirrorPersonalization;
            },
        },

        watch: {
            viewController(newValue, oldValue) {
                oldValue.off('stateChanged', this.stateChanged);
                newValue.on('stateChanged', this.stateChanged);
            },

            storeMirrorPersonalization() {
                this.checkSame();
            },
        },

        mounted() {
            this.viewController.on('stateChanged', this.stateChanged);

            this.checkSame();
        },

        methods: {
            stateChanged() {
                // State changed is automatically deferred.
                this.checkSame();
            },

            checkSame() {
                this.mirrorPersonalization = this.customizerStore.mirrorPersonalization;
            },

            selectPersonalizationType(selected) {
                this.customizerStore.undoCheckpoint();

                const mirrored = this.mirrorPersonalization;

                this.customizerStore.clearPersonalization(this.itemData);

                if (selected.id === 'personalization-logo') {
                    this.customizerStore.markPersonalized(this.logoPlacement);
                }

                if (selected.id === 'personalization-monogram') {
                    this.customizerStore.markPersonalized(this.textPlacement);
                }

                if (mirrored) {
                    let otherGroup;

                    if (this.itemData.code.includes('Left')) {
                        otherGroup = this.viewController.blueprint.groups.find((x) => x.code === 'PersonalizationRight');
                    } else {
                        otherGroup = this.viewController.blueprint.groups.find((x) => x.code === 'PersonalizationLeft');
                    }

                    this.customizerStore.clearPersonalization(otherGroup);

                    if (selected.id === 'personalization-logo') {
                        this.customizerStore.markPersonalized(otherGroup.placements.find((x) => x.code.startsWith('MONOGRAMMINGLOGO')));
                    }

                    if (selected.id === 'personalization-monogram') {
                        this.customizerStore.markPersonalized(otherGroup.placements.find((x) => x.code.startsWith('MONOGRAMMINGTEXT')));
                    }
                }
            },

            updateMirror(v) {
                this.customizerStore.undoCheckpoint();

                this.mirrorPersonalization = v;

                this.customizerStore.setMirrorPersonalization(v);

                if (v) {
                    this.customizerStore.mirrorToAnotherSide(this.itemData);
                }
            },
        },
    };
</script>
